import { PF_SEVERITY_COLOR, PF_STATUS_COLOR } from '@app/design/statusTokens';
import { AcmOverviewTokens } from './acmOverviewTokens';

export interface RegionDeviceCounts {
  online: number;
  degraded: number;
  offline: number;
}

export interface Region {
  id: string;
  cx: number;
  cy: number;
  label: string;
  devices: RegionDeviceCounts;
  jobs: number;
  trend: number;
}

export const regions: Region[] = [
  { id: 'NA', cx: 98, cy: 108, label: 'North America', devices: { online: 2660, degraded: 380, offline: 200 }, jobs: 48, trend: 4 },
  { id: 'LATAM', cx: 130, cy: 170, label: 'Latin America', devices: { online: 790, degraded: 130, offline: 80 }, jobs: 11, trend: -1 },
  { id: 'EU', cx: 330, cy: 84, label: 'Europe', devices: { online: 3930, degraded: 180, offline: 70 }, jobs: 61, trend: 1 },
  { id: 'AF', cx: 334, cy: 158, label: 'Africa', devices: { online: 292, degraded: 20, offline: 8 }, jobs: 4, trend: 0 },
  { id: 'MENA', cx: 410, cy: 132, label: 'Middle East', devices: { online: 540, degraded: 55, offline: 20 }, jobs: 6, trend: 2 },
  { id: 'APAC', cx: 556, cy: 116, label: 'Asia Pacific', devices: { online: 1950, degraded: 520, offline: 270 }, jobs: 27, trend: -3 },
];

export const CONNS = [[0, 2], [0, 1], [2, 4], [2, 3], [4, 5], [3, 4], [1, 3]] as const;

export function rTotal(r: Region) {
  return r.devices.online + r.devices.degraded + r.devices.offline;
}

export function rStatus(r: Region, C: AcmOverviewTokens) {
  const t = rTotal(r);
  if (r.devices.offline / t > 0.12 || r.devices.degraded / t > 0.2) {
    return { level: 'critical', color: C.red };
  }
  if (r.devices.degraded / t > 0.08) {
    return { level: 'warning', color: C.warning };
  }
  return { level: 'healthy', color: C.success };
}

export function arcD(x1: number, y1: number, x2: number, y2: number) {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2 - 26;
  return `M${x1} ${y1} Q${mx} ${my} ${x2} ${y2}`;
}

export const DOTS: { cx: number; cy: number }[] = [
  { cx: 18, cy: 56 }, { cx: 34, cy: 68 }, { cx: 50, cy: 58 }, { cx: 66, cy: 70 }, { cx: 82, cy: 60 },
  { cx: 98, cy: 72 }, { cx: 114, cy: 62 }, { cx: 130, cy: 52 }, { cx: 146, cy: 64 }, { cx: 162, cy: 54 },
  { cx: 178, cy: 66 }, { cx: 194, cy: 58 }, { cx: 210, cy: 70 }, { cx: 226, cy: 60 }, { cx: 242, cy: 52 },
  { cx: 258, cy: 64 }, { cx: 274, cy: 54 }, { cx: 290, cy: 66 }, { cx: 306, cy: 58 }, { cx: 322, cy: 48 },
  { cx: 338, cy: 60 }, { cx: 354, cy: 50 }, { cx: 370, cy: 62 }, { cx: 386, cy: 54 }, { cx: 402, cy: 66 },
  { cx: 418, cy: 56 }, { cx: 434, cy: 46 }, { cx: 450, cy: 58 }, { cx: 466, cy: 48 }, { cx: 482, cy: 60 },
  { cx: 498, cy: 54 }, { cx: 514, cy: 66 }, { cx: 530, cy: 56 }, { cx: 546, cy: 48 }, { cx: 562, cy: 60 },
  { cx: 578, cy: 52 }, { cx: 594, cy: 64 }, { cx: 610, cy: 54 }, { cx: 626, cy: 44 }, { cx: 642, cy: 56 },
  { cx: 14, cy: 88 }, { cx: 30, cy: 98 }, { cx: 46, cy: 90 }, { cx: 62, cy: 100 }, { cx: 78, cy: 92 },
  { cx: 94, cy: 102 }, { cx: 110, cy: 94 }, { cx: 126, cy: 104 }, { cx: 142, cy: 96 }, { cx: 158, cy: 106 },
  { cx: 174, cy: 98 }, { cx: 190, cy: 108 }, { cx: 206, cy: 100 }, { cx: 222, cy: 110 }, { cx: 238, cy: 102 },
  { cx: 254, cy: 112 }, { cx: 270, cy: 104 }, { cx: 286, cy: 114 }, { cx: 302, cy: 106 }, { cx: 318, cy: 96 },
  { cx: 334, cy: 108 }, { cx: 350, cy: 100 }, { cx: 366, cy: 110 }, { cx: 382, cy: 102 }, { cx: 398, cy: 112 },
  { cx: 414, cy: 104 }, { cx: 430, cy: 114 }, { cx: 446, cy: 106 }, { cx: 462, cy: 116 }, { cx: 478, cy: 108 },
  { cx: 494, cy: 98 }, { cx: 510, cy: 110 }, { cx: 526, cy: 102 }, { cx: 542, cy: 112 }, { cx: 558, cy: 104 },
  { cx: 574, cy: 114 }, { cx: 590, cy: 106 }, { cx: 606, cy: 96 }, { cx: 622, cy: 108 }, { cx: 638, cy: 100 },
  { cx: 22, cy: 122 }, { cx: 38, cy: 132 }, { cx: 54, cy: 124 }, { cx: 70, cy: 134 }, { cx: 86, cy: 126 },
  { cx: 102, cy: 136 }, { cx: 118, cy: 128 }, { cx: 134, cy: 138 }, { cx: 150, cy: 130 }, { cx: 166, cy: 140 },
  { cx: 182, cy: 132 }, { cx: 198, cy: 142 }, { cx: 214, cy: 134 }, { cx: 230, cy: 144 }, { cx: 246, cy: 136 },
  { cx: 262, cy: 146 }, { cx: 278, cy: 138 }, { cx: 294, cy: 148 }, { cx: 310, cy: 140 }, { cx: 326, cy: 130 },
  { cx: 342, cy: 142 }, { cx: 358, cy: 134 }, { cx: 374, cy: 144 }, { cx: 390, cy: 136 }, { cx: 406, cy: 126 },
  { cx: 422, cy: 138 }, { cx: 438, cy: 130 }, { cx: 454, cy: 140 }, { cx: 470, cy: 132 }, { cx: 486, cy: 142 },
  { cx: 502, cy: 134 }, { cx: 518, cy: 144 }, { cx: 534, cy: 136 }, { cx: 550, cy: 126 }, { cx: 566, cy: 138 },
  { cx: 582, cy: 128 }, { cx: 598, cy: 140 }, { cx: 614, cy: 130 }, { cx: 630, cy: 120 }, { cx: 646, cy: 132 },
  { cx: 30, cy: 156 }, { cx: 50, cy: 166 }, { cx: 70, cy: 158 }, { cx: 92, cy: 168 }, { cx: 114, cy: 160 },
  { cx: 136, cy: 170 }, { cx: 158, cy: 162 }, { cx: 180, cy: 172 }, { cx: 202, cy: 164 }, { cx: 224, cy: 174 },
  { cx: 246, cy: 166 }, { cx: 268, cy: 176 }, { cx: 290, cy: 168 }, { cx: 312, cy: 178 }, { cx: 334, cy: 170 },
  { cx: 356, cy: 180 }, { cx: 378, cy: 172 }, { cx: 400, cy: 162 }, { cx: 422, cy: 172 }, { cx: 444, cy: 164 },
  { cx: 466, cy: 174 }, { cx: 488, cy: 166 }, { cx: 510, cy: 176 }, { cx: 532, cy: 168 }, { cx: 554, cy: 160 },
  { cx: 576, cy: 170 }, { cx: 598, cy: 162 }, { cx: 620, cy: 152 }, { cx: 642, cy: 164 },
];

function mkStackedData(n = 12) {
  return Array.from({ length: n }, (_, i) => ({
    i,
    online: 15000 + Math.sin(i * 0.6) * 1500 + Math.random() * 800,
    degraded: 1800 + Math.sin(i * 0.5) * 300 + Math.random() * 200,
    offline: 900 + Math.sin(i * 0.7) * 200 + Math.random() * 150,
  }));
}

function mkJobsData(n = 14) {
  return Array.from({ length: n }, (_, i) => ({
    i,
    running: 120 + Math.sin(i * 0.8) * 20 + Math.random() * 15,
    failed: 16 + Math.sin(i * 0.5) * 6 + Math.random() * 4,
    queued: 18 + Math.sin(i * 0.6) * 6 + Math.random() * 5,
  }));
}

function mkEnrollmentData(n = 12) {
  return Array.from({ length: n }, (_, i) => ({
    i,
    v: 8 + i * 1.2 + Math.sin(i * 0.7) * 2 + Math.random() * 1.5,
  }));
}

export type KpiChartType = 'stackedArea' | 'bar' | 'stackedBar' | 'line';

export interface KpiItem {
  label: string;
  value: string;
  sub: string;
  pct: string;
  up: boolean;
  chartType: KpiChartType;
  chartData: Record<string, number>[];
  rows: { l: string; v: number }[];
  preview?: string[];
  footerAction?: string;
  badge?: string;
}

export const TOTAL_DEVICES = 21775;

export const KPIS: KpiItem[] = [
  {
    label: 'New devices',
    value: '23',
    sub: 'New devices onboarded',
    pct: '+5',
    up: true,
    chartType: 'line',
    chartData: mkEnrollmentData(),
    rows: [],
    footerAction: 'Review and approve',
    badge: 'Requires attention',
  },
  {
    label: 'Total Devices',
    value: TOTAL_DEVICES.toLocaleString(),
    sub: '+342 this week',
    pct: '+2.4%',
    up: true,
    chartType: 'stackedArea',
    chartData: mkStackedData(),
    rows: [{ l: 'Online', v: 18420 }, { l: 'Degraded', v: 2190 }, { l: 'Offline', v: 1165 }],
  },
  {
    label: 'Total Jobs',
    value: '192',
    sub: '23 failed',
    pct: '+18',
    up: true,
    chartType: 'bar',
    chartData: mkJobsData(),
    rows: [
      { l: 'Running', v: 142 },
      { l: 'Failed', v: 23 },
      { l: 'Queued', v: 27 },
    ],
  },
];

export const ALERTS = [
  {
    id: 6,
    lvl: 'critical',
    title: 'Alert 604',
    time: '14:08',
    msg: 'Unauthorized root login on 6 EU drone ground stations.',
    detail: 'Repeated failed-then-successful root SSH attempts on six Europe-theater UAS control nodes. Sessions are still open on the flight-ops network.',
    systems: 6,
  },
  {
    id: 1,
    lvl: 'critical',
    title: 'Alert 521',
    time: '13:42',
    msg: 'Critical vulnerability in billing service.',
    detail: 'CVE-2024-3094 is present on 8 billing-edge devices. Advisory RHSA-2024:1842 is available.',
    systems: 8,
  },
  {
    id: 2,
    lvl: 'warning',
    title: 'Alert 122',
    time: '12:58',
    msg: 'Software version out of date on 3 hosts.',
    detail: 'Three Europe-region hosts are two minor versions behind the approved fleet image.',
    systems: 3,
  },
  {
    id: 3,
    lvl: 'critical',
    title: 'Alert 318',
    time: '11:30',
    msg: 'Kernel exploit flagged — update required.',
    detail: 'CVE-2024-1086 affects 14 kernel-edge systems. Apply RHSA-2024:1200 before the next rollout.',
    systems: 14,
  },
  {
    id: 4,
    lvl: 'warning',
    title: 'Alert 207',
    time: '10:17',
    msg: '2FA not enabled on admin accounts.',
    detail: 'Four admin accounts in Charlie Services can sign in without a second factor.',
    systems: 4,
  },
  {
    id: 5,
    lvl: 'info',
    title: 'Alert 102',
    time: '09:45',
    msg: 'Patching completed on 142 containers.',
    detail: 'The latest security rollup finished on 142 containers across NA and EU. No failed nodes.',
    systems: 142,
  },
];

export type SecuritySeverity = 'critical' | 'important' | 'moderate' | 'minor';

export const SECURITY_FEED_LEGEND: { label: string; severity: SecuritySeverity; color: string; count: number }[] = [
  { label: 'Critical', severity: 'critical', color: PF_SEVERITY_COLOR.critical, count: 12 },
  { label: 'Important', severity: 'important', color: PF_SEVERITY_COLOR.important, count: 47 },
  { label: 'Moderate', severity: 'moderate', color: PF_SEVERITY_COLOR.moderate, count: 214 },
  { label: 'Minor', severity: 'minor', color: PF_SEVERITY_COLOR.minor, count: 89 },
];

export const SECURITY_FEED_TOTAL = SECURITY_FEED_LEGEND.reduce((sum, item) => sum + item.count, 0);

export const SECURITY_SYSTEMS_TOTAL = 178;
export const SECURITY_SYSTEMS_LEGEND: { label: string; key: 'affected' | 'healthy'; color: string; count: number }[] = [
  { label: 'Affected', key: 'affected', color: PF_STATUS_COLOR.danger, count: 37 },
  { label: 'Healthy', key: 'healthy', color: PF_STATUS_COLOR.success, count: 141 },
];

export interface SecurityFeedRow {
  id: string;
  cve: string;
  severity: SecuritySeverity;
  advisory: string;
  affected: number;
  application: string;
}

export const SECURITY_FEED_ROWS: SecurityFeedRow[] = [
  {
    id: '1',
    cve: 'CVE-2024-3094',
    severity: 'critical',
    advisory: 'RHSA-2024:1842',
    affected: 8,
    application: 'billing-edge',
  },
  {
    id: '2',
    cve: 'CVE-2024-21626',
    severity: 'important',
    advisory: 'RHSA-2024:1601',
    affected: 5,
    application: 'fleet-agent',
  },
  {
    id: '3',
    cve: 'CVE-2023-38545',
    severity: 'moderate',
    advisory: 'RHSA-2023:5455',
    affected: 5,
    application: 'gateway-proxy',
  },
  {
    id: '4',
    cve: 'CVE-2023-4911',
    severity: 'minor',
    advisory: 'RHSA-2023:5453',
    affected: 5,
    application: 'node-exporter',
  },
  {
    id: '5',
    cve: 'CVE-2024-1086',
    severity: 'critical',
    advisory: 'RHSA-2024:1200',
    affected: 14,
    application: 'kernel-edge',
  },
];

/** @deprecated Prefer SECURITY_FEED_LEGEND */
export const VULNERABILITY_DATA = SECURITY_FEED_LEGEND.map((item) => ({
  label: item.label,
  value: item.count,
  slices: [{ pct: 100, o: 1 }],
}));

/** @deprecated Prefer SECURITY_FEED_LEGEND */
export const STATUS_DATA = VULNERABILITY_DATA;

/** @deprecated Prefer SECURITY_FEED_LEGEND */
export const VULNERABILITY_LEGEND = SECURITY_FEED_LEGEND.map(({ label, color }) => ({ label, color }));

export function polar(cx: number, cy: number, r: number, deg: number) {
  const a = (deg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

export function sweepArc(cx: number, cy: number, r: number, pct: number) {
  if (pct <= 0) return null;
  if (pct >= 1) pct = 0.9999;
  const s = polar(cx, cy, r, 0);
  const e = polar(cx, cy, r, pct * 360);
  return `M${s.x} ${s.y} A${r} ${r} 0 ${pct > 0.5 ? 1 : 0} 1 ${e.x} ${e.y}`;
}
